import { useLayoutEffect, useState } from "react";

const readPaddingTop = (element: HTMLElement) => {
    const padding = Number.parseFloat(getComputedStyle(element).paddingTop);
    return Number.isNaN(padding) ? 0 : padding;
};

/** The scroller's content top, in viewport coordinates. */
const readContentTop = (scroller: HTMLElement) =>
    scroller.getBoundingClientRect().top - scroller.scrollTop;

const readListStart = (list: HTMLElement, contentTop: number) =>
    Math.round(
        list.getBoundingClientRect().top - contentTop + readPaddingTop(list),
    );

interface ScrollerLists {
    observer: ResizeObserver;
    setters: Map<HTMLElement, (listStart: number) => void>;
}

const listsByScroller = new WeakMap<HTMLElement, ScrollerLists>();

/**
 * One observer per scroller, shared by its windowed lists. Something above a
 * list only moves it by resizing: the scroller, a row before the lists, or
 * another list (lanes are as tall as their lists). Any of them resizing
 * re-measures every list of the scroller.
 */
const getScrollerLists = (scroller: HTMLElement) => {
    const existing = listsByScroller.get(scroller);
    if (existing !== undefined) {
        return existing;
    }
    const setters = new Map<HTMLElement, (listStart: number) => void>();
    const observer = new ResizeObserver(() => {
        const contentTop = readContentTop(scroller);
        setters.forEach((setListStart, list) =>
            setListStart(readListStart(list, contentTop)),
        );
    });
    observer.observe(scroller);
    Array.from(scroller.children).forEach((child) => observer.observe(child));
    const lists = { observer, setters };
    listsByScroller.set(scroller, lists);
    return lists;
};

/**
 * Where the list's first item sits in its scroller's content. Scrolling
 * doesn't move it, so it is read when the list mounts and again when the
 * layout around it resizes.
 */
export const useListStart = (
    list: HTMLElement | null,
    scroller: HTMLElement | null,
) => {
    const [listStart, setListStart] = useState(0);
    useLayoutEffect(() => {
        if (list === null || scroller === null) {
            return undefined;
        }
        const lists = getScrollerLists(scroller);
        lists.setters.set(list, setListStart);
        // The border box, so a padding change (room for a gap `+`) counts.
        lists.observer.observe(list, { box: "border-box" });
        setListStart(readListStart(list, readContentTop(scroller)));
        return () => {
            lists.setters.delete(list);
            lists.observer.unobserve(list);
            if (lists.setters.size === 0) {
                lists.observer.disconnect();
                listsByScroller.delete(scroller);
            }
        };
    }, [list, scroller]);
    return listStart;
};
